import { Component, Output, EventEmitter, OnInit, Input } from '@angular/core';
import { FormControl, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { Usuario, TipoUsuario, PuestoUsuario } from '../../../interfaces/usuario';
import { UsuarioService } from '../../../providers/usuario.service';
import { AuthenticationService } from '../../../providers/auth.service';
import { MatDialog } from '@angular/material';
import { AutorizadorDialog } from '../../../components/autorizador/autorizador.component';
import { Telefono } from '../../../components/telefono/telefono.component';

/**
 * Verificar que las contraseñas sean iguales:
 * - Al crear usuario se verifica siempre
 * - Al editar usuario se verifica solo si ingresaron una contraseña para editar la actual
 * - Si es editar y no ingresan contraseña, no es necesario ingresar ninguna, se mantiene la actual
 */
export function verificarContraseñaValidatorFn(IdUsuario, contraCtrl, verifCtrl): ValidatorFn {
  return (control: AbstractControl): ValidationErrors => {
    // Si es editar y no cambiaron la contraseña no es necesario verificar
    if (IdUsuario && !contraCtrl.value) {
      return null;
    }

    // Si es crear o ingresaron una contraseña hay que verificar que sean iguales
    if (!control.value) {
      return <ValidationErrors>{
        'required': true // la contraseña es requerida
      };
    } else {
      if (contraCtrl.value != verifCtrl.value) {
        return <ValidationErrors>{
          'distintas': true // contraseñas distintas
        };
      }
    }

    return null;
  }
}

@Component({
  selector: 'app-usuario',
  templateUrl: './usuario.component.html',
  styleUrls: ['./usuario.component.scss']
})
export class UsuarioComponent implements OnInit {
  @Input() IdUsuario?: number | string;
  usuario: Usuario;

  tiposUsuario = <TipoUsuario[]>[];
  cargandoTipos: boolean = false;
  puestosUsuario = <PuestoUsuario[]>[];
  cargandoPuestos: boolean = false;

  hide: boolean;
  hide2: boolean;

  @Output() onUsuarioInput = new EventEmitter(); // datos del formulario cambian
  @Output() formState = new EventEmitter(); // emite el estado del formulario

  usuarioFormControl = new FormControl('', [
    Validators.required
  ]);
  emailFormControl = new FormControl('', [
    Validators.required,
    Validators.email,
  ]);
  tipoUsuarioFormControl = new FormControl('', [
    Validators.required
  ]);
  contrasenaFormControl = new FormControl('', []);
  contrasenaVerificadaFormControl = new FormControl('', []);

  constructor(
    private usuariosService: UsuarioService,
    public dialog: MatDialog,
    public authenticationService: AuthenticationService
  ) { }

  ngOnInit(): void {
    this.contrasenaFormControl.setValidators([
      verificarContraseñaValidatorFn(this.IdUsuario, this.contrasenaFormControl, this.contrasenaVerificadaFormControl)
    ]);
    this.contrasenaVerificadaFormControl.setValidators([
      verificarContraseñaValidatorFn(this.IdUsuario, this.contrasenaFormControl, this.contrasenaVerificadaFormControl)
    ]);

    this.usuario = new Usuario();

    if (this.IdUsuario != null) {
      // Inhabilitar campos no editables
      this.emailFormControl.disable();
      this.usuarioFormControl.disable();

      // si me estoy editando a mi mismo no puedo cambiar el tipo de usuario
      if (this.authenticationService.IdUsuario == this.IdUsuario) {
        this.tipoUsuarioFormControl.disable();
      }

      this.formState.emit({ cargando: true });
      this.usuariosService.getUsuarios(
        {
          page: null,
          pageSize: null
        },
        {
          sort: null,
          order: null
        },
        {
          IdUsuario: this.IdUsuario
        }
      ).subscribe(
        respuesta => {
          if (respuesta.estado == 1 && respuesta.data) {
            var usuario = respuesta.data.usuarios[0];
            console.log('usuario', usuario);
            if (usuario) {
              this.usuario = new Usuario(
                usuario.id,
                usuario.usuario,
                usuario.email,
                new TipoUsuario(
                  usuario.tipoUsuario.id,
                  usuario.tipoUsuario.descripcion,
                ),
                null,
                usuario.nombre,
                usuario.apellido,
                new Telefono(usuario.telefono.pais, usuario.telefono.area, usuario.telefono.numero),
                +usuario.estado,
                usuario.puesto,
                !!+usuario.editable,
                !!+usuario.eliminable
              );

              this.emailFormControl.setValue(this.usuario.email);
              this.usuarioFormControl.setValue(this.usuario.usuario);
              this.tipoUsuarioFormControl.setValue(this.usuario.tipoUsuario);

              this.formState.emit({ // datos encontrados
                estado: 1,
                mensaje: respuesta.mensaje,
                cargando: false
              });
            } else {
              this.formState.emit({ // error al buscar datos
                estado: 3,
                mensaje: 'No se encontraron los datos buscados',
                cargando: false
              });
            }
          }
        },
        error => {
          this.formState.emit({ // error con el servidor
            estado: 2,
            mensaje: error,
            cargando: false
          });
        }
      );
    }

    this.traerTiposUsuario();
    this.traerPuestos();
  }

  traerPuestos(): void {
    this.puestosUsuario = <PuestoUsuario[]>[];
    this.cargandoPuestos = true;
    this.usuariosService.getPuestos().subscribe(
      respuesta => {
        if (respuesta.estado == 1 && respuesta.data && respuesta.data.puestos) {
          respuesta.data.puestos.forEach(puesto => {
            this.puestosUsuario.push(new PuestoUsuario(
              puesto.id,
              puesto.descripcion
            ));
          });
        }

        this.cargandoPuestos = false;
      },
      () => {
        this.cargandoPuestos = false;
      }
    );
  }

  traerTiposUsuario(): void {
    this.tiposUsuario = <TipoUsuario[]>[];
    this.cargandoTipos = true;
    this.usuariosService.getTiposUsuario(
      {
        page: null,
        pageSize: null
      },
      {
        sort: null,
        sortOrder: null
      },
      {
        generico: '',
        verTodos: 1
      }
    ).subscribe(
      respuesta => {
        console.log('TIPO DE USUARIO', respuesta);
        if (respuesta.estado == 1 && respuesta.data) {
          respuesta.data.forEach(tipoUsuario => {
            this.tiposUsuario.push(new TipoUsuario(
              tipoUsuario.IdTipoUsuario,
              tipoUsuario.TipoUsuarioDesc
            ));
          });
        } else {
          this.formState.emit({ // error al buscar datos
            estado: 3,
            mensaje: 'El usuario no podrá ser asignado'
          });
        }

        this.cargandoTipos = false;
      },
      error => {
        this.cargandoTipos = false;

        this.formState.emit({ // error al buscar datos
          estado: 2,
          mensaje: error
        });
      }
    );
  }

  onInput(): void { // cuando cambia un dato del formulario emitimos un evento
    this.onUsuarioInput.emit();
  }

  onContraseniaChange(): void {
    // al cambiar la contraseña verificar que sea igual a la verificada
    this.contrasenaFormControl.updateValueAndValidity();
    this.contrasenaFormControl.markAsTouched();
    this.contrasenaVerificadaFormControl.updateValueAndValidity();
    this.contrasenaVerificadaFormControl.markAsTouched();
  }

  compararTipoUsuario(dato1: TipoUsuario, dato2: TipoUsuario): boolean {
    if (dato1 && dato2) {
      return dato1.id == dato2.id;
    }

    return false;
  }

  compararPuesto(puesto1: PuestoUsuario, puesto2: PuestoUsuario): boolean {
    if (puesto1 && puesto2) {
      return puesto1.id == puesto2.id;
    }

    return false;
  }

  getUsuario(): Promise<Usuario> {
    return new Promise<Usuario>((resolve, reject) => {
      if (this.emailFormControl.hasError('email') ||
        this.emailFormControl.hasError('required') ||
        this.contrasenaFormControl.hasError('required') ||
        this.contrasenaFormControl.hasError('distintas') ||
        this.contrasenaVerificadaFormControl.hasError('required') ||
        this.contrasenaVerificadaFormControl.hasError('distintas') ||
        this.tipoUsuarioFormControl.hasError('required') ||
        this.usuarioFormControl.hasError('required')) {
        // marcar inputs como usados para mostrar el error si es necesario
        this.usuarioFormControl.markAsTouched();
        this.emailFormControl.markAsTouched();
        this.tipoUsuarioFormControl.markAsTouched();
        this.contrasenaFormControl.markAsTouched();
        this.contrasenaVerificadaFormControl.markAsTouched();

        throw "Datos incorrectos";
      }

      // Si ingresaron una contraseña, hay que enviar ambas al servidor (la nueva y la verificación)
      // casos de uso: CREAR ó EDITAR y durante la edición ingresan una contraseña nueva
      if (this.contrasenaFormControl.value) {
        Object.assign(
          this.usuario,
          {
            contrasena2: this.contrasenaVerificadaFormControl.value
          }
        );
      }

      // Si se requiere un permiso especial
      if (this.requiereAutorizacionEspecial()) {
        // solicitamos al usuario el/los dato/s necesario/s para la verificación
        let dialogAutorizar = this.dialog.open(AutorizadorDialog, {
          disableClose: true
        });

        dialogAutorizar.afterClosed().subscribe(
          autorizacion => {
            if (autorizacion) {
              // setear el campo de autorización para verificar en el servidor
              if (this.usuario) {
                Object.assign(this.usuario, { contrasenaActual: autorizacion });
              }

              resolve(this.usuario);
            } else {
              reject("Esta acción requiere autorización");
            }
          }
        );
      } else {
        // si no requiere permisos especiales
        resolve(this.usuario);
      }
    });
  }

  // lógica para determinar si requiere autorización especial
  requiereAutorizacionEspecial(): boolean {
    if (this.usuario) {
      // si es editar
      if (this.IdUsuario != null) {
        // si quieren cambiar la contraseña
        if (this.contrasenaFormControl.value) {
          return true;
        }
      }
    }

    return false;
  }
}
